"use client"

import { Loader2, Search } from "lucide-react"

import { avatarHex, stageTint, TINT } from "@/lib/stage-colors"
import { cn } from "@/lib/utils"
import type { SearchRow } from "@/hooks/use-searches"

/**
 * Saved searches, from canvas artboard 7a.
 *
 * Each search is a card rather than a table row, because a search is a
 * paragraph and a set of criteria before it is a set of columns: the title and
 * its result count, the brief behind a rule, the criteria as pills, and a
 * footer saying which job it feeds, who saved it and when.
 *
 * Purely presentational; everything arrives from `useSearches`.
 */

function Criterion({ label, value }: { label: string; value: string }) {
  return (
    <span className="inline-flex items-baseline gap-1.5 rounded-full border px-2.5 py-[5px]">
      <span className="text-[10.5px] text-muted-foreground">{label}</span>
      <span className="max-w-[280px] truncate text-[11.5px]">{value}</span>
    </span>
  )
}

function SearchCard({
  row,
  onOpen,
}: {
  row: SearchRow
  onOpen: (row: SearchRow) => void
}) {
  const hex = avatarHex(row.owner?.name ?? row.id)

  return (
    <div className="rounded-[10px] border bg-card px-[18px] py-4">
      <div className="flex items-baseline gap-2.5">
        <Search className="size-[15px] flex-none translate-y-[2px] text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate text-[14.5px] font-semibold leading-[1.2]">
          {row.title}
        </span>
        {row.results !== undefined && (
          <>
            <span className="ml-auto whitespace-nowrap font-mono text-[13px] font-medium">
              {row.results}
            </span>
            <span className="whitespace-nowrap text-[11px] text-muted-foreground">
              results
            </span>
          </>
        )}
      </div>

      {row.brief && (
        // A rule rather than a quotation mark: the brief is what was asked for,
        // set apart from the row's own writing.
        <div className="mt-3 flex gap-2.5">
          <span aria-hidden className="w-[2px] flex-none rounded-sm bg-border" />
          <p className="text-[13px] leading-[1.65]">{row.brief}</p>
        </div>
      )}

      {row.criteria.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {row.criteria.map((criterion) => (
            <Criterion key={criterion.label} {...criterion} />
          ))}
        </div>
      )}

      <div className="mt-3.5 flex flex-wrap items-center gap-x-2.5 gap-y-2 border-t pt-3">
        {row.feeds ? (
          <>
            <span className="text-[12px] text-muted-foreground">feeds</span>
            <span className="min-w-0 truncate text-[12px]">
              {row.feeds.title}
              {row.feeds.company && (
                <span className="text-muted-foreground"> @{row.feeds.company}</span>
              )}
            </span>
          </>
        ) : (
          <span className="text-[12px] text-muted-foreground">feeds no job yet</span>
        )}

        {row.owner && (
          <span
            title={row.owner.name}
            className="flex size-6 flex-none items-center justify-center rounded-full text-[9px] font-medium"
            style={{ color: hex, backgroundColor: stageTint(hex, TINT.pill) }}
          >
            {row.owner.initials}
          </span>
        )}

        {row.when && (
          <span className="whitespace-nowrap font-mono text-[11px] text-muted-foreground">
            {row.when}
          </span>
        )}

        <button
          type="button"
          onClick={() => onOpen(row)}
          disabled={!row.hash}
          title={row.hash ? undefined : "This search has no stored results to open"}
          className="ml-auto whitespace-nowrap text-[12px] font-medium text-brand-ink transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Open results
        </button>
      </div>
    </div>
  )
}

export function GraphiteSearches({
  rows,
  jobOptions,
  search,
  onSearchChange,
  job,
  onJobChange,
  total,
  onOpen,
  isLoading,
  isError,
}: {
  rows: SearchRow[]
  jobOptions: { id: string; title: string }[]
  search: string
  onSearchChange: (next: string) => void
  job: string
  onJobChange: (next: string) => void
  total: number
  onOpen: (row: SearchRow) => void
  isLoading: boolean
  isError: boolean
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-none flex-wrap items-center gap-2 px-4 pb-3 pt-4 @2xl:px-[26px]">
        <span className="relative inline-flex w-full items-center gap-2 rounded-md border bg-card px-3 py-[9px] @2xl:w-[248px]">
          <Search className="size-3.5 flex-none text-muted-foreground" />
          <input
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search saved searches"
            aria-label="Search saved searches"
            className="min-w-0 flex-1 bg-transparent text-[12.5px] outline-none placeholder:text-muted-foreground"
          />
        </span>

        {jobOptions.length > 0 && (
          <span className="relative inline-flex items-center gap-[7px] rounded-md border bg-card py-2 pl-3 pr-[30px] text-[12px] text-muted-foreground">
            {job === "all"
              ? "All jobs"
              : (jobOptions.find((option) => option.id === job)?.title ?? "All jobs")}
            <select
              value={job}
              onChange={(event) => onJobChange(event.target.value)}
              aria-label="All jobs"
              className="absolute inset-0 cursor-pointer opacity-0"
            >
              <option value="all">All jobs</option>
              {jobOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.title}
                </option>
              ))}
            </select>
          </span>
        )}

        <span className="ml-auto whitespace-nowrap text-[11.5px] text-muted-foreground">
          {isLoading || isError ? (
            "—"
          ) : (
            <>
              {total} search{total === 1 ? "" : "es"}
              {rows.length !== total && ` · ${rows.length} shown`}
            </>
          )}
        </span>
      </div>

      <div
        className={cn(
          "min-h-0 flex-1 space-y-3 overflow-y-auto px-4 pb-[26px] @2xl:px-[26px]",
        )}
      >
        {isLoading && (
          <div className="flex items-center gap-2 py-4 text-[12.5px] text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" />
            Loading searches…
          </div>
        )}

        {isError && (
          <p className="py-4 text-[12.5px] text-muted-foreground">
            Searches could not be loaded.
          </p>
        )}

        {!isLoading && !isError && rows.length === 0 && (
          <p className="py-6 text-[12.5px] text-muted-foreground">
            {total === 0
              ? "No saved searches yet. Run one and it will be kept here."
              : "No search matches these filters."}
          </p>
        )}

        {/*
          Rows only once every join has landed. `/jobs` and `/orgObs/users`
          supply each row's owner and linked job, so rendering on `/search`
          alone showed cards with those facts blank — beside a loading message
          saying the page was not ready. The aggregated flags reached this
          component and gated only the message.
        */}
        {!isLoading &&
          !isError &&
          rows.map((row) => <SearchCard key={row.id} row={row} onOpen={onOpen} />)}
      </div>
    </div>
  )
}
